'use client';

// MaxBidsProvider — the transport/plumbing ancestor for the admin MAX-BID stream
// (BL-1552). Replaces `RealtimeMaxBidsProvider`, which was auction-keyed.
//
// GENERIC, NOT AUCTION-KEYED — and that is the whole point. It takes a
// `subscriber` and a `catchupFetcher` and nothing else, exactly like
// `LedgerProvider`. It owns the per-lot store registry and installs the global
// wake/reconnect triggers; it knows nothing about which auction, or which lot, is
// on screen.
//
// WHY THAT MATTERS (BL-1552 AC 6, and the 2026-09-04 addendum). The subscription
// has to be owned by the CONSUMING component, keyed on the lot id it already
// holds. If the replacement for the auction-wide provider were itself keyed on an
// auction, this item would have removed the payload problem and left the ancestor
// problem: a bid-record unit that cannot mount anywhere the auction-keyed
// ancestors are absent. That ancestor requirement is what blocks BL-1542, and
// removing it is half of why this item exists.
//
// So: consumers call `useLotMaxBids(lotId)` / `useLotPreBids(lotId, …)`, and this
// provider supplies only the transport they ride on.

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    type ReactNode,
} from 'react';

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { EntityStateStoreRegistry } from '../../stores/realtime/entity-state-registry';
import { installReconcileTriggers } from '../../stores/realtime/install-reconcile-triggers';
import { MaxBidStateStore, type MaxBidStateCatchupFetcher } from '../../stores/realtime/max-bid-state-store';
import type { NotifyScheduler } from '../../stores/realtime/entity-state-store';

/**
 * NO TIMER (BL-1629 AC 4). This stream now carries a dense, commit-ordered per-LOT
 * sequence, so the client can SEE a dropped delta as a hole and catch up exactly
 * once — the blind poll it used to need as its only defence is gone.
 *
 * It was 30_000, matching the lot + registrations streams' cadence (ADR 042 — Lots
 * Machinery §1). That poll existed because a per-entity LWW cursor makes a missing
 * message invisible: with no way to detect a gap, re-reading on a timer was the only
 * option, and it ran in every open tab whether or not anything had changed.
 *
 * `installReconcileTriggers` treats `0` as "install no interval" and leaves focus,
 * visibilitychange, online and transport-reconnect untouched (RAC 4) — those are the
 * wake-ups that matter, and none of them is a poll.
 *
 * `RealtimeRegistrationsProvider` still carries 30_000 and MUST keep it until BL-1630
 * sequences that stream: removing its safety net before it can detect a gap would
 * remove its only gap defence.
 */
const RECONCILE_INTERVAL_MS = 0;

const MaxBidRegistryContext =
    createContext<EntityStateStoreRegistry<MaxBidStateStore> | null>(null);

export interface MaxBidsProviderProps {
    /** Transport subscriber (shared with the app's RealtimeStack). */
    subscriber: RealtimeSubscriber;
    /**
     * Authenticated PER-LOT catch-up fetcher (mints the topic + cold-seed). Takes
     * a lot id. MUST be a stable reference — the registry is memoised on it, so a
     * fresh closure each render would rebuild every live subscription.
     */
    catchupFetcher: MaxBidStateCatchupFetcher;
    /** Override the reconcile interval (tests pass `0` to disable). */
    reconcileIntervalMs?: number;
    /** Override the reconcile throttle window (BUG-1175). */
    reconcileThrottleMs?: number;
    /**
     * Override the coalesced-notify scheduler (BUG-1407). Tests inject a manual
     * pump so "one render per burst" is an exact assertion rather than a race.
     */
    scheduleNotify?: NotifyScheduler;
    children: ReactNode;
}

/**
 * Mount once above anything that renders admin max-bid state. Owns the per-lot
 * store registry AND the global wake/reconnect triggers.
 */
export function MaxBidsProvider({
    subscriber,
    catchupFetcher,
    reconcileIntervalMs = RECONCILE_INTERVAL_MS,
    reconcileThrottleMs,
    scheduleNotify,
    children,
}: MaxBidsProviderProps) {
    const registry = useMemo(
        () =>
            new EntityStateStoreRegistry<MaxBidStateStore>(
                (lotId) =>
                    new MaxBidStateStore(subscriber, catchupFetcher, lotId, scheduleNotify),
            ),
        [subscriber, catchupFetcher, scheduleNotify],
    );

    useEffect(() => {
        return installReconcileTriggers({
            reconcileAll: () => registry.reconcileAll(),
            subscriber,
            intervalMs: reconcileIntervalMs,
            reconcileThrottleMs,
        });
    }, [registry, subscriber, reconcileIntervalMs, reconcileThrottleMs]);

    return (
        <MaxBidRegistryContext.Provider value={registry}>
            {children}
        </MaxBidRegistryContext.Provider>
    );
}

/**
 * The per-lot store registry. Internal to the max-bid hooks.
 *
 * @throws when no `<MaxBidsProvider>` is above the caller — a missing transport
 *   ancestor must fail loudly rather than render a permanently empty ceiling list,
 *   which is indistinguishable from a lot that genuinely has none.
 */
export function useMaxBidRegistry(): EntityStateStoreRegistry<MaxBidStateStore> {
    const registry = useContext(MaxBidRegistryContext);
    if (!registry) {
        throw new Error('useLotMaxBids/useLotPreBids must be used inside a <MaxBidsProvider>');
    }
    return registry;
}

/**
 * The same registry, or `null` when no `<MaxBidsProvider>` is mounted above.
 *
 * The non-throwing twin of `useMaxBidRegistry`, and the seam `LotBidRecord` uses
 * to answer "is my transport already mounted?" (BL-1542). The unit renders at
 * three sites and only one of them — the clerk console — mounts this provider,
 * so it supplies its own where there is none. Reading the context nullably is
 * what makes that decidable without a prop: `useMaxBidRegistry` throws BEFORE it
 * could tell you anything, which is the ancestor requirement itself.
 *
 * @returns the registry, or `null` off-console.
 */
export function useOptionalMaxBidRegistry(): EntityStateStoreRegistry<MaxBidStateStore> | null {
    return useContext(MaxBidRegistryContext);
}
